import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setAuthToken } from '../../../services/api';

const AuthContext = createContext();

const IDP_URL = process.env.REACT_APP_IDP_URL || 'https://idp.admin-shell-io.com';
const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || '').trim().toLowerCase();
const AUTH_DISABLED = ['local', 'disabled', 'dev', 'none'].includes(AUTH_MODE);

// Get API base URL using same logic as api.js
const getAPIBase = () => {
  const hasWindow = typeof window !== 'undefined';
  if (hasWindow && window.__DISCOVERY_API_URL__) {
    return String(window.__DISCOVERY_API_URL__).replace(/\/+$/, '');
  }
  const envValue = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.trim() : '';
  const hostname = hasWindow ? window.location.hostname : '';
  const protocol = hasWindow ? window.location.protocol : '';
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '' ||
    hostname.endsWith('.local');

  // Priority 1: Use explicit env var if provided
  if (envValue !== '') {
    const envIsAbsoluteUrl = envValue.startsWith('http://') || envValue.startsWith('https://');
    const envIsLocalUrl =
      envValue.includes('localhost') ||
      envValue.includes('127.0.0.1') ||
      envValue.includes('://0.0.0.0');

    // If the env value points to localhost but we're not on localhost, ignore it and use relative URL
    if (hasWindow && !isLocalHost && envIsLocalUrl) {
      return '';
    }

    if (envValue.startsWith('/')) {
      const normalized = envValue.replace(/\/+$/, '');
      if (normalized === '' || normalized === '/api') {
        return '';
      }
      return normalized.startsWith('/') ? normalized : `/${normalized}`;
    }

    if (envIsAbsoluteUrl) {
      return envValue;
    }

    return `/${envValue.replace(/^\/+/, '')}`;
  }

  // Priority 2: Runtime detection - for production domains use relative URL
  if (hasWindow && !isLocalHost) {
    return '';
  }

  // Priority 3: If running on localhost but via HTTPS (dev over secure tunnel), still use relative path
  if (hasWindow && isLocalHost && protocol === 'https:') {
    return '';
  }

  // Priority 4: NODE_ENV hints production
  if (process.env.NODE_ENV === 'production') {
    return '';
  }

  // Development fallback - explicit localhost for local dev scenarios
  return 'http://localhost:50008';
};

const API_BASE = getAPIBase();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const isEmbeddedRuntime = () => {
  if (typeof window === 'undefined') return false;
  if (window.__DISCOVERY_EMBEDDED__) return true;
  try {
    return window.self !== window.top;
  } catch (_) {
    return true;
  }
};

export const AuthProvider = ({ children, embedded = false }) => {
  const embeddedMode = embedded || isEmbeddedRuntime();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [jwtToken, setJwtToken] = useState(null);

  const fetchIdpAccessToken = useCallback(async () => {
    if (AUTH_DISABLED) {
      return null;
    }
    try {
      const tokenResponse = await fetch(`${IDP_URL}/api/auth/token/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });

      const contentType = tokenResponse.headers.get('content-type') || '';
      if (!tokenResponse.ok || !contentType.includes('application/json')) {
        console.warn('Failed to fetch IDP token:', tokenResponse.status);
        return null;
      }

      const tokenData = await tokenResponse.json();
      if (tokenData?.access_token) {
        setAuthToken(tokenData.access_token);
        return tokenData.access_token;
      }

      console.warn('IDP token response missing access_token');
      return null;
    } catch (error) {
      console.error('Error fetching IDP token:', error);
      return null;
    }
  }, []);

  // Check authentication status with Django IDP
  const checkAuthStatus = useCallback(async () => {
    try {
      setLoading(true);

      if (AUTH_DISABLED) {
        setUser({
          id: 'local-user',
          username: 'local-user',
          email: 'local@adx-discovery.local'
        });
        setIsAuthenticated(true);
        setJwtToken(null);
        setAuthToken(null);
        return;
      }
      
      // Build the API URL
      const apiUrl = API_BASE ? `${API_BASE}/api/idp/session-check` : '/api/idp/session-check';
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Checking auth status at:', apiUrl, 'API_BASE:', API_BASE);
      }
      
      // Call our backend which will validate with IDP
      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include', // CRITICAL: Include cookies
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('Response is not JSON, got:', contentType);
        setUser(null);
        setIsAuthenticated(false);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.authenticated && data.user) {
          setUser(data.user);
          setIsAuthenticated(true);
          setJwtToken(null);
          setAuthToken(null);
          console.log('User authenticated:', data.user.username);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setJwtToken(null);
          setAuthToken(null);
          console.log('ℹ️ User not authenticated');
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setJwtToken(null);
        setAuthToken(null);
        console.log('ℹ️ Authentication check failed with status:', response.status);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
      setIsAuthenticated(false);
      setJwtToken(null);
      setAuthToken(null);
    } finally {
      setLoading(false);
    }
  }, [fetchIdpAccessToken]);

  const login = () => {
    if (AUTH_DISABLED) {
      setIsAuthenticated(true);
      return;
    }
    // Redirect to IDP login
    const currentUrl = window.location.href;
    const loginUrl = `${IDP_URL}/login/?next=${encodeURIComponent(currentUrl)}`;
    window.location.href = loginUrl;
  };

  const logout = async () => {
    try {
      if (AUTH_DISABLED) {
        setUser(null);
        setIsAuthenticated(false);
        setJwtToken(null);
        setAuthToken(null);
        return;
      }

      // Build the API URL
      const apiUrl = API_BASE ? `${API_BASE}/api/idp/logout` : '/api/idp/logout';
      
      // Call our backend logout endpoint
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (response.ok && contentType && contentType.includes('application/json')) {
        const data = await response.json();
        
        // Clear local state
        setUser(null);
        setIsAuthenticated(false);
        setJwtToken(null);
        setAuthToken(null);
        
        // Redirect to IDP logout
        if (data.logout_url) {
          window.location.href = data.logout_url;
        }
      } else {
        // Clear local state anyway
        setUser(null);
        setIsAuthenticated(false);
        setJwtToken(null);
        setAuthToken(null);
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear local state anyway
      setUser(null);
      setIsAuthenticated(false);
      setJwtToken(null);
      setAuthToken(null);
    }
  };

  const getJWTToken = async (audience = 'default') => {
    if (jwtToken) {
      return jwtToken;
    }
    const token = await fetchIdpAccessToken();
    if (token) {
      setJwtToken(token);
    } else {
      setAuthToken(null);
    }
    return token;
  };

  const getTokenForAudience = async (audience) => {
    // Return stored JWT token for any audience
    return jwtToken;
  };

  const clearTokenCache = () => {
    setJwtToken(null);
    setAuthToken(null);
  };

  useEffect(() => {
    checkAuthStatus();

    const urlParams = new URLSearchParams(window.location.search);
    let timeoutId;
    if (urlParams.get('auth') === 'success') {
      timeoutId = setTimeout(() => {
        checkAuthStatus();
      }, 500);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [checkAuthStatus]);

  useEffect(() => {
    if (AUTH_DISABLED) return;
    if (embeddedMode) return;
    if (loading) return;
    if (isAuthenticated) return;
    if (typeof window === 'undefined') return;
    const runtime = window.__DISCOVERY_IDP_URL__;
    const envVar = typeof process !== 'undefined' && process.env && process.env.REACT_APP_IDP_URL;
    const isLocal =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    const idpBase = (runtime || envVar || (isLocal ? 'http://localhost:50005' : '')).replace(/\/+$/, '');
    if (!idpBase) return;
    const target = `${idpBase}/login/?next=${encodeURIComponent(window.location.href)}`;
    window.location.replace(target);
  }, [embeddedMode, loading, isAuthenticated]);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    getJWTToken,
    getTokenForAudience,
    checkAuthStatus,
    clearTokenCache
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
